import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const alertVariants = cva(
  'relative w-full rounded-lg border border-neutrals-N200 p-4 [&>svg~*]:pl-7 [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-neutrals-N1000 dark:border-neutrals-N800 dark:[&>svg]:text-neutrals-N200',
  {
    variants: {
      variant: {
        default:
          'bg-white text-neutrals-N1000 dark:bg-neutrals-N1000 dark:text-neutrals-N200',
        destructive:
          'border-primary-red-R500/50 text-primary-red-R500 dark:border-primary-red-R500 [&>svg]:text-primary-red-R500 dark:border-primary-red-R700/50 dark:text-primary-red-R700 dark:dark:border-primary-red-R700 dark:[&>svg]:text-primary-red-R700',
        success:
          'border-primary-green-G500/50 text-primary-green-G600 dark:text-primary-green-G400 bg-primary-green-G100/20 dark:bg-primary-green-G950/50 [&>svg]:text-primary-green-G600 dark:[&>svg]:text-primary-green-G400 *:data-[slot=alert-description]:text-primary-green-G600/90 dark:*:data-[slot=alert-description]:text-primary-green-G400/90',
        info: 'border-primary-blue-B300 text-primary-blue-B600 dark:text-primary-blue-B300 bg-primary-blue-B100 dark:bg-primary-green-G950/50 [&>svg]:text-primary-blue-B600 dark:[&>svg]:text-primary-blue-B500 *:data-[slot=alert-description]:text-primary-blue-B600',
        warning:
          'border-primary-orange-O500 bg-primary-orange-O100 [&>svg]:text-primary-orange-O500 *:data-[slot=alert-description]:text-neutrals-N800 ',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

const Alert = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>
>(({ className, variant, ...props }, ref) => (
  <div
    ref={ref}
    role="alert"
    className={cn(alertVariants({ variant }), className)}
    {...props}
  />
));
Alert.displayName = 'Alert';

const AlertTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h5
    ref={ref}
    className={cn(
      'mb-1 text-sm font-semibold leading-none tracking-tight',
      className,
    )}
    {...props}
  />
));
AlertTitle.displayName = 'AlertTitle';

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'text-xs text-neutrals-N800 [&_p]:leading-relaxed',
      className,
    )}
    {...props}
  />
));
AlertDescription.displayName = 'AlertDescription';

export { Alert, AlertTitle, AlertDescription };
